import { useState } from 'react';
import { ActionIcon, Button, Group, Select, Stack, Table, Text } from '@mantine/core';
import type { ContractorDetail } from '@isg/shared-types';
import {
  EVRAK_BEKLENTI_DURUMU_ETIKETI,
  EVRAK_EKSENI_ETIKETI,
  evrakPaketiMaliyetMetni,
} from '@isg/validation';
import type { EvrakBeklentisi, EvrakPaketiMaliyetOzeti } from '@isg/validation';
import {
  useEvrakPaketleri,
  useYukleniciEvrakBeklentisi,
  useYukleniciEvrakEkiEkle,
  useYukleniciEvrakEkiKaldir,
  useYukleniciPaketAta,
  useYukleniciPaketOnizleme,
} from '../api/queries';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { Bolum, KapiSebebi, Rozet, SolukSatir, YazamamaSebebi } from '../sozlesme';
import { ANLAM, type RozetAnlami } from '../theme';
import { QueryStatus } from './parts';

/**
 * YÜKLENİCİ EVRAK PAKETİ YÜZEYİ (MODÜL KARARLARI PAKETİ · D1 — 17.09.2026;
 * Kapsam §14 K-102a…k).
 *
 * ⚠️ **EKRAN HESAPLAMAZ:** kalem listesi, durumlar ve maliyet özeti sunucudan
 * (`evrak-beklentisi` · `onizleme`); kapı ve sebep `yetkiler.paketKapisi`nden.
 * Kart ve portal AYNI yol planı ucunu okur (K-102f).
 */

type BeklentiKalemi = EvrakBeklentisi['kalemler'][number];

function durumAnlami(durum: BeklentiKalemi['durum']): RozetAnlami {
  if (durum === 'tamam') return 'olumlu';
  if (durum === 'bekliyor') return 'dikkat';
  return 'engel';
}

function DurumHucresi({ kalem }: { kalem: BeklentiKalemi }): React.JSX.Element {
  return (
    <Group gap={6}>
      <Rozet anlam={durumAnlami(kalem.durum)} testId="beklenti-durumu">
        {EVRAK_BEKLENTI_DURUMU_ETIKETI[kalem.durum]}
      </Rozet>
      {kalem.eksen === 'kisi' && kalem.kisiSayisi !== null && kalem.eksikKisiSayisi !== null && (
        <Text size="xs" c="dimmed" data-testid="beklenti-kisi-sayisi">
          {kalem.kisiSayisi === 0
            ? 'aktif personel yok'
            : `${kalem.eksikKisiSayisi}/${kalem.kisiSayisi} kişide eksik`}
        </Text>
      )}
    </Group>
  );
}

/**
 * YOL PLANI (K-102f) — beklenen ama sahayı açmayan kalemler. *"Talep et"*
 * eylemi yoktur: talep listenin kendisidir.
 */
export function YolPlani({ beklenti }: { beklenti: EvrakBeklentisi }): React.JSX.Element {
  const eksikler = beklenti.kalemler.filter((k) => k.durum !== 'tamam');
  return (
    <Stack gap={4} data-testid="yol-plani">
      <Text size="sm" fw={500}>
        Yol planı
      </Text>
      {eksikler.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="yol-plani-tamam">
          İstenen evrakların hepsi tamam.
        </Text>
      ) : (
        eksikler.map((kalem) => (
          <Text key={kalem.evrakTipiId} size="sm" data-testid="yol-plani-satiri">
            {`${kalem.evrakTipiAd}${kalem.eksen === 'kisi' ? ' (kişi)' : ''} — zorunlu · ${EVRAK_BEKLENTI_DURUMU_ETIKETI[kalem.durum]}`}
          </Text>
        ))
      )}
    </Stack>
  );
}

function BeklentiTablosu({
  kalemler,
  testId,
  kaldir,
  sonSatir,
}: {
  kalemler: BeklentiKalemi[];
  testId: string;
  kaldir?: (kalem: BeklentiKalemi) => void;
  sonSatir?: React.ReactNode;
}): React.JSX.Element {
  return (
    <Table data-testid={testId}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Evrak</Table.Th>
          <Table.Th>Eksen</Table.Th>
          <Table.Th>Durum</Table.Th>
          {kaldir && <Table.Th />}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {kalemler.map((kalem) => (
          <Table.Tr key={kalem.evrakTipiId} data-testid={`${testId}-satiri`}>
            <Table.Td>{kalem.evrakTipiAd}</Table.Td>
            <Table.Td>{EVRAK_EKSENI_ETIKETI[kalem.eksen]}</Table.Td>
            <Table.Td>
              <DurumHucresi kalem={kalem} />
            </Table.Td>
            {kaldir && (
              <Table.Td>
                <ActionIcon
                  variant="subtle"
                  color={ANLAM.engel}
                  aria-label={`${kalem.evrakTipiAd} ekini kaldır`}
                  data-testid="ozel-ek-kaldir"
                  onClick={() => kaldir(kalem)}
                >
                  ✕
                </ActionIcon>
              </Table.Td>
            )}
          </Table.Tr>
        ))}
        {sonSatir}
      </Table.Tbody>
    </Table>
  );
}

/** Portal (K-102f): ne yüklemesi gerektiği, durumlarıyla — tek liste. */
export function PortalYolPlani({ contractorId }: { contractorId: string }): React.JSX.Element {
  const beklenti = useYukleniciEvrakBeklentisi(contractorId);
  return (
    <Stack gap="xs" data-testid="portal-yol-plani">
      <QueryStatus queries={[beklenti]} />
      {beklenti.data && (
        <>
          <YolPlani beklenti={beklenti.data} />
          <BeklentiTablosu kalemler={beklenti.data.kalemler} testId="portal-beklenen-evrak" />
        </>
      )}
    </Stack>
  );
}

function MaliyetOzeti({
  ozet,
  testId,
}: {
  ozet: EvrakPaketiMaliyetOzeti;
  testId: string;
}): React.JSX.Element {
  return (
    <Stack gap={2} data-testid={testId}>
      <Text size="sm" data-testid={`${testId}-metni`}>
        {evrakPaketiMaliyetMetni(ozet)}
      </Text>
      {ozet.dusenKalemler.length > 0 && (
        <Text size="xs" c="dimmed" data-testid={`${testId}-dusen`}>
          Listeden düşecek: {ozet.dusenKalemler.join(', ')}
        </Text>
      )}
      {ozet.eklenenKalemler.length > 0 && (
        <Text size="xs" c="dimmed" data-testid={`${testId}-eklenen`}>
          Yeni istenecek: {ozet.eklenenKalemler.join(', ')}
        </Text>
      )}
    </Stack>
  );
}

export { MaliyetOzeti };

/**
 * YÜKLENİCİ KARTI — EVRAK PAKETİ BÖLÜMÜ: paket (değişim önizlemeli) ·
 * paketten gelen · bu firmaya özel · yol planı.
 */
export function EvrakPaketiBolumu({ firma }: { firma: ContractorDetail }): React.JSX.Element {
  const beklenti = useYukleniciEvrakBeklentisi(firma.id);
  const kapi = firma.yetkiler.paketKapisi;
  const paketler = useEvrakPaketleri({ enabled: kapi.yazabilir });
  const [secilenPaket, setSecilenPaket] = useState<string | null>(null);
  const degisecek = secilenPaket !== null && secilenPaket !== firma.evrakPaketi.id;
  const onizleme = useYukleniciPaketOnizleme(firma.id, degisecek ? secilenPaket : null);
  const ata = useYukleniciPaketAta();
  const ekEkle = useYukleniciEvrakEkiEkle();
  const ekKaldir = useYukleniciEvrakEkiKaldir();
  const [yeniEk, setYeniEk] = useState<string | null>(null);
  useKaydedilmemisIcerik(yeniEk !== null ? 1 : 0, 'özel evrak satırı');

  const paketKalemleri = beklenti.data?.kalemler.filter((k) => k.kaynak === 'paket') ?? [];
  const ozelEkler = beklenti.data?.kalemler.filter((k) => k.kaynak === 'ek') ?? [];

  return (
    <Bolum baslik="Evrak paketi" testId="evrak-paketi-bolumu">
      <Stack gap="sm">
        <Group gap="sm" align="flex-end">
          <Text size="sm" data-testid="evrak-paketi-adi">
            Paket: <b>{firma.evrakPaketi.ad}</b>
          </Text>
          {kapi.yazabilir && paketler.data && (
            <Select
              size="xs"
              aria-label="Paketi değiştir"
              placeholder="Paketi değiştir"
              data-testid="evrak-paketi-sec"
              comboboxProps={{ withinPortal: true }}
              data={paketler.data.map((p) => ({ value: p.id, label: p.ad }))}
              value={secilenPaket ?? firma.evrakPaketi.id}
              allowDeselect={false}
              onChange={(deger) => setSecilenPaket(deger)}
            />
          )}
        </Group>
        <KapiSebebi
          durum={firma.yetkiler.paketKapisi}
          nesne="İstenecek evrakı"
          fiil="belirler"
          testId="evrak-paketi-yazamama-sebebi"
        />
        {degisecek && (
          <Stack gap="xs" data-testid="paket-degisim-onizleme">
            <QueryStatus queries={[onizleme]} />
            {onizleme.data && <MaliyetOzeti ozet={onizleme.data} testId="paket-maliyet" />}
            {ata.isError && (
              <Text size="sm" c={ANLAM.engel} role="alert" data-testid="paket-degisim-hata">
                {ata.error.message}
              </Text>
            )}
            <Group gap="xs">
              <Button
                size="xs"
                data-testid="paket-degistir"
                disabled={!onizleme.data}
                loading={ata.isPending}
                onClick={() => {
                  if (secilenPaket)
                    ata.mutate(
                      { contractorId: firma.id, evrakPaketiId: secilenPaket },
                      { onSuccess: () => setSecilenPaket(null) },
                    );
                }}
              >
                Paketi değiştir
              </Button>
              <Button
                size="xs"
                variant="default"
                data-testid="paket-degisim-vazgec"
                onClick={() => setSecilenPaket(null)}
              >
                Vazgeç
              </Button>
            </Group>
          </Stack>
        )}
        <QueryStatus queries={[beklenti]} />
        {beklenti.data && (
          <>
            <YolPlani beklenti={beklenti.data} />
            <Text size="sm" fw={500}>
              Paketten gelen
            </Text>
            {paketKalemleri.length === 0 ? (
              <Text size="sm" c="dimmed" data-testid="paket-bos">
                Paket evrak istemiyor.
              </Text>
            ) : (
              <BeklentiTablosu kalemler={paketKalemleri} testId="paketten-gelen" />
            )}
            {kapi.yazabilir && paketKalemleri.length > 0 && (
              <YazamamaSebebi testId="paket-kalemi-cikarilamaz">
                Paketten gelen evrak çıkarılamaz — daha hafif bir paket seçin.
              </YazamamaSebebi>
            )}
            <Text size="sm" fw={500}>
              Bu firmaya özel
            </Text>
            <BeklentiTablosu
              kalemler={ozelEkler}
              testId="ozel-ek"
              kaldir={
                kapi.yazabilir
                  ? (kalem) => {
                      if (kalem.ekId) ekKaldir.mutate({ contractorId: firma.id, ekId: kalem.ekId });
                    }
                  : undefined
              }
              sonSatir={
                kapi.yazabilir ? (
                  <SolukSatir
                    testId="ozel-ek-soluk-satir"
                    dokunuldu={yeniEk !== null}
                    kaydedilebilir={yeniEk !== null}
                    kaydediliyor={ekEkle.isPending}
                    kolonSayisi={3}
                    onVazgec={() => setYeniEk(null)}
                    onKaydet={() => {
                      if (yeniEk)
                        ekEkle.mutate(
                          { contractorId: firma.id, evrakTipiId: yeniEk },
                          { onSuccess: () => setYeniEk(null) },
                        );
                    }}
                    ikinciKatman={
                      ekEkle.isError ? (
                        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="ozel-ek-hata">
                          {ekEkle.error.message}
                        </Text>
                      ) : undefined
                    }
                  >
                    <Table.Td colSpan={2}>
                      <Select
                        size="xs"
                        placeholder="Evrak ekle"
                        data-testid="ozel-ek-sec"
                        searchable
                        comboboxProps={{ withinPortal: true }}
                        data={beklenti.data.eklenebilirKalemler.map((k) => ({
                          value: k.evrakTipiId,
                          label: `${k.evrakTipiAd} (${EVRAK_EKSENI_ETIKETI[k.eksen]})`,
                        }))}
                        value={yeniEk}
                        onChange={setYeniEk}
                      />
                    </Table.Td>
                    <Table.Td />
                  </SolukSatir>
                ) : undefined
              }
            />
            {ekKaldir.isError && (
              <Text size="sm" c={ANLAM.engel} role="alert" data-testid="ozel-ek-kaldir-hata">
                {ekKaldir.error.message}
              </Text>
            )}
          </>
        )}
      </Stack>
    </Bolum>
  );
}
